import React, { useEffect, useRef, useState, useCallback, Component } from 'react';
import useStore from '../store/useStore';
import PTTButton from './PTTButton';
import UserList from './UserList';
import AudioWave from './AudioWave';
import MessageLog from './MessageLog';
import axios from 'axios';
import { Hash, Users, AlertTriangle, X, Send, Image as ImageIcon, Mic, Trash2, Video } from 'lucide-react';
import { getSocket } from '../hooks/useSocket';
import { useNavigate } from 'react-router-dom';

// Mini error boundary to isolate which child crashes
class MiniEB extends Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(e) { return { err: e }; }
  render() {
    if (this.state.err) return (
      <div style={{ padding: '8px 10px', color: '#FF3B30', fontSize: '11px', background: 'rgba(255,59,48,0.08)', border: '1px solid rgba(255,59,48,0.18)', borderRadius: '10px', margin: '4px' }}>
        ❌ {this.props.name}: {this.state.err.message}
      </div>
    );
    return this.props.children;
  }
}

const EMPTY_ARRAY = [];

// Panic button — hold 3 seconds to activate
function PanicButton({ channelId }) {
  const user = useStore(s => s.user);
  const addToast = useStore(s => s.addToast);
  const [showModal, setShowModal] = useState(false);
  const [holdProgress, setHoldProgress] = useState(0);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const holdTimerRef = useRef(null);
  const progressRef = useRef(null);
  const startTimeRef = useRef(null);
  const HOLD_MS = 3000;

  const startHold = () => {
    startTimeRef.current = Date.now();
    setHoldProgress(0);
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const pct = Math.min(100, (elapsed / HOLD_MS) * 100);
      setHoldProgress(pct);
      if (pct >= 100) {
        clearInterval(progressRef.current);
        setHoldProgress(0);
        setShowModal(true);
      }
    }, 30);
  };

  const endHold = () => {
    if (progressRef.current) clearInterval(progressRef.current);
    setHoldProgress(0);
  };

  const sendPanic = async () => {
    setSending(true);
    try {
      const socket = getSocket();
      let lat = null, lng = null;

      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
          );
          lat = pos.coords.latitude;
          lng = pos.coords.longitude;
        } catch (_) {}
      }

      if (socket?.connected) {
        socket.emit('panic:alert', {
          channelId: channelId || null,
          lat,
          lng,
          message: 'ALERTA DE PÁNICO',
        });
      }

      setSent(true);
      setShowModal(false);
      addToast('🚨 Alerta de emergencia enviada', 'error');
      setTimeout(() => setSent(false), 10000);
    } catch (err) {
      addToast('Error al enviar alerta', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        {holdProgress > 0 && (
          <svg style={{ position: 'absolute', top: -4, left: -4, width: 44, height: 44, transform: 'rotate(-90deg)' }}>
            <circle cx={22} cy={22} r={18} fill="none" stroke="rgba(255,45,85,0.7)" strokeWidth={3}
              strokeDasharray={`${2 * Math.PI * 18}`}
              strokeDashoffset={`${2 * Math.PI * 18 * (1 - holdProgress / 100)}`}
              style={{ transition: 'stroke-dashoffset 0.03s linear' }}
            />
          </svg>
        )}
        <button
          title="Botón de Pánico — Mantén 3 segundos"
          onMouseDown={startHold}
          onMouseUp={endHold}
          onMouseLeave={endHold}
          onTouchStart={e => { e.preventDefault(); startHold(); }}
          onTouchEnd={endHold}
          style={{
            width: 36, height: 36,
            borderRadius: '50%',
            border: sent ? '2px solid #FF2D55' : '1px solid rgba(255,45,85,0.30)',
            background: sent ? 'rgba(255,45,85,0.20)' : '#FFE5EC',
            color: sent ? '#FF2D55' : '#FF375F',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.2s',
            userSelect: 'none',
            boxShadow: sent ? '0 6px 18px rgba(255,45,85,0.35)' : 'none',
          }}
        >
          <AlertTriangle size={16} />
        </button>
      </div>

      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'saturate(180%) blur(40px)',
            border: '1px solid rgba(255,45,85,0.20)',
            borderRadius: 20,
            padding: '28px 24px',
            maxWidth: 360,
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 40px 90px rgba(0,0,0,.18), 0 10px 30px rgba(255,45,85,.14)',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🚨</div>
            <h2 style={{ color: '#FF2D55', fontWeight: 800, fontSize: 18, marginBottom: 8, letterSpacing: '-0.4px' }}>
              ¿Enviar Alerta de Emergencia?
            </h2>
            <p style={{ color: '#6E6E73', fontSize: 13, marginBottom: 20 }}>
              Se notificará a todos los administradores con tu ubicación actual.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 10,
                  border: 'none',
                  background: '#F5F5F7',
                  color: '#48484A', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}
              >
                Cancelar
              </button>
              <button
                onClick={sendPanic}
                disabled={sending}
                style={{
                  flex: 1, padding: '10px 0', borderRadius: 10,
                  border: 'none',
                  background: 'linear-gradient(135deg,#FF2D55,#FF3B30)',
                  color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                  boxShadow: '0 8px 22px rgba(255,45,85,.45)',
                }}
              >
                {sending ? 'Enviando...' : '🚨 Enviar Alerta'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── ClearChannelButton — admin-only, deletes messages + audio ──────────────
function ClearChannelButton({ channelId, channelName }) {
  const [confirm, setConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const setMessages = useStore(s => s.setMessages);

  const handleClear = async (what) => {
    setClearing(true);
    try {
      const res = await axios.delete(`/api/admin/channels/${channelId}/clear?what=${what}`);
      const d = res.data;
      // Clear local store too
      if (what !== 'audio') setMessages(channelId, []);
      alert(`✅ Limpieza completada:\n• ${d.messages || 0} mensajes eliminados\n• ${d.transmissions || 0} audios eliminados\n• ${d.filesDeleted || 0} archivos borrados del disco`);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setClearing(false);
      setConfirm(false);
    }
  };

  if (confirm) return (
    <div style={{ position: 'relative' }}>
      <div style={{
        position: 'absolute', top: '110%', right: 0, zIndex: 50, minWidth: 230,
        background: 'rgba(255,255,255,0.94)',
        backdropFilter: 'saturate(180%) blur(30px)',
        border: '1px solid rgba(0,0,0,0.06)',
        borderRadius: 14, padding: 14,
        boxShadow: '0 20px 50px rgba(0,0,0,.14), 0 4px 12px rgba(0,0,0,.06)',
      }}>
        <p style={{ fontSize: 12, color: '#1D1D1F', fontWeight: 700, marginBottom: 10 }}>
          🗑 Borrar en #{channelName}:
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button onClick={() => handleClear('messages')} disabled={clearing}
            style={{ padding: '7px 12px', background: '#FFE5EC', border: 'none', borderRadius: 10, color: '#FF375F', fontSize: 12, cursor: 'pointer', textAlign: 'left', fontWeight: 600 }}>
            💬 Solo mensajes de chat
          </button>
          <button onClick={() => handleClear('audio')} disabled={clearing}
            style={{ padding: '7px 12px', background: '#FFE5EC', border: 'none', borderRadius: 10, color: '#FF375F', fontSize: 12, cursor: 'pointer', textAlign: 'left', fontWeight: 600 }}>
            🎙 Solo audios (PTT)
          </button>
          <button onClick={() => handleClear('all')} disabled={clearing}
            style={{ padding: '7px 12px', background: 'linear-gradient(135deg,#FF2D55,#FF3B30)', border: 'none', borderRadius: 10, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', textAlign: 'left', boxShadow: '0 6px 16px rgba(255,45,85,.35)' }}>
            {clearing ? 'Borrando…' : '⚠️ Borrar TODO'}
          </button>
          <button onClick={() => setConfirm(false)}
            style={{ padding: '6px 12px', background: '#F5F5F7', border: 'none', borderRadius: 10, color: '#48484A', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <button onClick={() => setConfirm(true)} title="Borrar historial del canal (solo admin)"
      style={{ padding: '6px 8px', background: '#FFE5EC', border: 'none', borderRadius: 10, color: '#FF375F', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
      <Trash2 size={13} />
    </button>
  );
}

// ── ChatInput — text + image sender ──────────────────────────────────────
function ChatInput({ channelId }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const fileRef = useRef();
  const socket = getSocket();

  const sendText = useCallback(async (e) => {
    e?.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      if (socket?.connected) {
        socket.emit('channel:sendMessage', { channelId, content: text.trim(), type: 'text' });
        setText('');
      }
    } finally {
      setSending(false);
    }
  }, [text, sending, channelId, socket]);

  const sendImage = useCallback(async () => {
    if (!previewFile || sending) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('image', previewFile);
      await axios.post(`/api/channels/${channelId}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(null);
      setPreviewFile(null);
    } catch (err) {
      console.error('[ChatInput] upload error:', err);
    } finally {
      setSending(false);
    }
  }, [previewFile, channelId, sending]);

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreviewFile(file);
    const url = URL.createObjectURL(file);
    setPreview(url);
    e.target.value = '';
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); }
  };

  if (!channelId) return null;

  return (
    <div style={{ padding: '0 12px 10px', flexShrink: 0 }}>
      {preview && (
        <div style={{ marginBottom: 6, position: 'relative', display: 'inline-block' }}>
          <img src={preview} alt="preview" style={{ maxHeight: 100, maxWidth: 200, borderRadius: 10, border: '1px solid rgba(0,0,0,0.08)' }} />
          <button onClick={() => { setPreview(null); setPreviewFile(null); }}
            style={{ position: 'absolute', top: -6, right: -6, background: '#FF3B30', border: 'none', borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', color: '#fff', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,.15)' }}>
            ✕
          </button>
          <button onClick={sendImage} disabled={sending}
            style={{ display: 'block', marginTop: 4, padding: '4px 12px', background: 'linear-gradient(135deg,#007AFF,#AF52DE)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 11, fontWeight: 700, cursor: sending ? 'not-allowed' : 'pointer', boxShadow: '0 4px 10px rgba(0,122,255,.30)' }}>
            {sending ? 'Enviando…' : '📤 Enviar imagen'}
          </button>
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
        <button onClick={() => fileRef.current?.click()} title="Adjuntar imagen"
          style={{ flexShrink: 0, padding: '8px', background: '#F5F5F7', border: 'none', borderRadius: 10, color: '#48484A', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
          <ImageIcon size={15} />
        </button>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onFileChange} style={{ display: 'none' }} />
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Escribe un mensaje… (Enter para enviar)"
          rows={1}
          style={{
            flex: 1, padding: '9px 12px',
            background: '#fff',
            border: '1px solid rgba(0,0,0,0.10)', borderRadius: 10,
            color: '#1D1D1F',
            fontSize: 13, resize: 'none', outline: 'none', fontFamily: 'inherit',
            lineHeight: '1.4', maxHeight: 80, overflowY: 'auto',
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,.03)',
          }}
        />
        <button onClick={sendText} disabled={!text.trim() || sending}
          style={{
            flexShrink: 0, padding: '9px 11px',
            background: text.trim() ? 'linear-gradient(135deg,#007AFF,#AF52DE)' : '#F5F5F7',
            border: 'none', borderRadius: 10,
            color: text.trim() ? '#fff' : '#AEAEB2',
            cursor: text.trim() ? 'pointer' : 'default',
            display: 'flex', alignItems: 'center',
            boxShadow: text.trim() ? '0 4px 10px rgba(0,122,255,.28)' : 'none',
          }}>
          <Send size={15} />
        </button>
      </div>
    </div>
  );
}

export default function ChannelView() {
  const currentChannel = useStore(s => s.currentChannel);
  const currentUser    = useStore(s => s.user);
  const isAdmin        = currentUser?.role === 'admin';
  const setMessages    = useStore(s => s.setMessages);
  const addMessage     = useStore(s => s.addMessage);
  const channelUsers   = useStore(s => s.channelUsers);
  const channelId      = currentChannel?.id;
  const navigate       = useNavigate();

  useEffect(() => {
    if (!channelId) return;
    axios.get(`/api/channels/${channelId}/messages`)
      .then(res => setMessages(channelId, res.data))
      .catch(() => {});
  }, [channelId]); // eslint-disable-line

  // Listen for broadcast messages from the server
  useEffect(() => {
    const socket = getSocket();
    if (!socket || !channelId) return;

    const handleBroadcast = (data) => {
      // Only show if it's for our current channel or no channel specified
      addMessage(channelId, {
        id: `broadcast-${data.id}-${Date.now()}`,
        type: 'broadcast',
        name: data.name,
        message: data.message,
        timestamp: data.timestamp || new Date().toISOString(),
      });
    };

    socket.on('broadcast:message', handleBroadcast);
    return () => { socket.off('broadcast:message', handleBroadcast); };
  }, [channelId, addMessage]); // eslint-disable-line

  if (!currentChannel) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="w-24 h-24 rounded-xl flex items-center justify-center mb-6 shadow-apple-md"
          style={{ background: 'rgba(255,255,255,0.78)', border: '1px solid rgba(0,0,0,0.05)' }}>
          <span className="text-5xl">📡</span>
        </div>
        <h2 className="text-xl font-bold text-n-800 mb-2 tracking-apple-tighter">Selecciona un canal</h2>
        <p className="text-n-500 text-sm max-w-xs">Elige un canal en la barra lateral para comenzar a transmitir</p>
      </div>
    );
  }

  const usersInChannel = channelUsers[channelId] ?? EMPTY_ARRAY;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="px-6 py-4 flex items-center gap-3 flex-shrink-0"
        style={{
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          background: 'rgba(255,255,255,0.65)',
          WebkitBackdropFilter: 'saturate(180%) blur(20px)',
          backdropFilter: 'saturate(180%) blur(20px)',
        }}>
        <div className="w-9 h-9 rounded-md flex items-center justify-center shadow-apple-sm"
          style={{ background: '#E6F1FF', color: '#007AFF' }}>
          <Hash size={16} />
        </div>
        <div>
          <h2 className="font-bold text-n-800 text-base leading-tight tracking-apple-tighter">{currentChannel.name}</h2>
          {currentChannel.description && <p className="text-xs text-n-500 mt-0.5">{currentChannel.description}</p>}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => navigate(`/video/${channelId}`)}
            title="Iniciar videollamada"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
            style={{ background: '#F4E9FB', color: '#AF52DE' }}
          >
            <Video size={12} /> Video
          </button>
          {isAdmin && <ClearChannelButton channelId={channelId} channelName={currentChannel.name} />}
          <PanicButton channelId={channelId} />
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full"
            style={{ background: '#F5F5F7' }}>
            <Users size={12} className="text-n-500" />
            <span className="text-xs font-semibold text-n-700">{usersInChannel.length}</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        <div className="flex-1 flex flex-col overflow-hidden">
          <MiniEB name="AudioWave"><AudioWave /></MiniEB>
          <div className="flex-1 overflow-hidden">
            <MiniEB name="MessageLog"><MessageLog channelId={channelId} /></MiniEB>
          </div>
          <MiniEB name="ChatInput"><ChatInput channelId={channelId} /></MiniEB>
          <div className="px-4 pb-4 flex-shrink-0">
            <MiniEB name="PTTButton"><PTTButton /></MiniEB>
          </div>
        </div>
        <aside className="w-full lg:w-56 flex-shrink-0 lg:border-l" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
          <MiniEB name="UserList"><UserList channelId={channelId} /></MiniEB>
        </aside>
      </div>
    </div>
  );
}
